import { TENANT_ROLES, type Invitation, type InvitationStatus, type TenantRole } from "@fts/core";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { ConfirmDialog, CopyButton, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { Note, PageHeader, Panel, RoleBadge } from "../components/ui";
import type { Member } from "../data";
import { useI18n } from "../i18n";
import { initials } from "../lib/fleet";
import { formatDate, inviteLink, relativeTime } from "../lib/format";
import { assignableRoles, canManageMember, isTenantAdmin } from "../lib/permissions";
import { useSession, type TenantData } from "../state/session";
import { siteOptions } from "./SiteForm";

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const INVITE_TONE: Record<InvitationStatus, string> = { pending: "warn", accepted: "ok", revoked: "neutral", expired: "neutral" };

/* ---------------- dialogs ---------------- */

function RoleDialog({ member, onClose }: { member: Member; onClose: () => void }) {
  const { t } = useI18n();
  const { provider, tenant, user, refresh } = useSession();
  const [role, setRole] = useState<TenantRole>(member.role);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const roles = user ? assignableRoles(user) : [];
  const scoped = member.siteIds.length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenant || role === member.role) return onClose();
    setBusy(true);
    setError(null);
    try {
      await provider.updateMemberRole(tenant.id, member.userId, role);
      await refresh();
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t("members.roleTitle", { name: member.displayName })}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <FormField id="member-role" label={t("users.role")} hint={t(`users.roleHelp.${role}`)}>
        <select {...fieldAria("member-role", { hint: true })} value={role} onChange={(e) => setRole(e.target.value as TenantRole)} data-autofocus>
          {roles.map((r) => (
            <option key={r} value={r} disabled={r === "admin" && scoped}>
              {t(`role.${r}`)}
              {r === "admin" && scoped ? ` — ${t("members.adminNeedsAllSites")}` : ""}
            </option>
          ))}
        </select>
      </FormField>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

/** One-time view after creating an invitation: send it by e-mail or copy the link. */
export function InviteLinkView({ invitation, token, link, onClose }: { invitation: Invitation; token: string; link: string; onClose: () => void }) {
  const { t, locale } = useI18n();
  const { provider } = useSession();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<unknown>(null);

  const send = async () => {
    setSending(true);
    setSendError(null);
    try {
      await provider.sendInvitationEmail(invitation.id, token);
      setSent(true);
    } catch (err) {
      setSendError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      dismissible={false}
      title={t("invite.createdTitle")}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={`button ${sent ? "button-primary" : "button-secondary"}`} onClick={onClose} disabled={sending}>
            {t("common.close")}
          </button>
          <button type="button" className={`button ${sent ? "button-secondary" : "button-primary"}`} onClick={() => void send()} disabled={sending}>
            <Icon name="mail" size={16} />
            {sending ? t("invite.sending") : sent ? t("invite.sendAgain") : t("invite.sendEmail")}
          </button>
        </>
      }
    >
      <p className="dialog-text">
        {t("invite.createdBody", {
          email: invitation.email,
          role: t(`role.${invitation.role}`),
          date: formatDate(invitation.expiresAt, locale) ?? "",
        })}
      </p>
      <div className="field">
        <label htmlFor="invite-link">{t("invite.link")}</label>
        <span className="secret-cell secret-cell-lg">
          <input id="invite-link" className="mono" readOnly value={link} onFocus={(e) => e.currentTarget.select()} data-autofocus />
          <CopyButton text={link} label={t("invite.copyLink")} />
        </span>
      </div>
      {sent && (
        <p className="success-banner" role="status">
          <Icon name="check" size={18} />
          <span>
            {t("invite.emailSent", { email: invitation.email })}
            {provider.mode === "demo" && ` ${t("invite.emailSentDemo")}`}
          </span>
        </p>
      )}
      {sendError !== null && <FormError error={sendError} />}
      <p className="note">
        <Icon name="mail" size={16} />
        <span>{t("invite.sendHint")}</span>
      </p>
    </Dialog>
  );
}

function InviteDialog({ data, onClose, onCreated }: { data: TenantData; onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const { provider, tenant, user } = useSession();
  const roles = user ? assignableRoles(user) : [];
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>("viewer");
  const [scope, setScope] = useState<"all" | "sites">("all");
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<{ link: string; token: string; invitation: Invitation } | null>(null);
  const sites = useMemo(() => siteOptions(data.sites), [data.sites]);
  const isAdminRole = role === "admin";

  const errors = {
    email: !email.trim() ? t("form.required") : !EMAIL_PATTERN.test(email.trim()) ? t("error.invalid_email") : null,
    scope: !isAdminRole && scope === "sites" && siteIds.length === 0 ? t("invite.chooseSites") : null,
  };
  const shown = (k: keyof typeof errors) => (touched ? errors[k] : null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!tenant || Object.values(errors).some(Boolean)) return;
    setBusy(true);
    setError(null);
    try {
      const { invitation, token } = await provider.createInvitation({
        tenantId: tenant.id,
        email: email.trim(),
        role,
        siteIds: isAdminRole || scope === "all" ? [] : siteIds,
      });
      setResult({ invitation, token, link: inviteLink(token) });
      setBusy(false);
      onCreated();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  if (result) return <InviteLinkView invitation={result.invitation} token={result.token} link={result.link} onClose={onClose} />;

  return (
    <Dialog
      title={t("invite.title")}
      description={t("invite.description", { tenant: tenant?.name ?? "" })}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("invite.submit")}
          </button>
        </>
      }
    >
      <FormField id="invite-email" label={t("users.email")} error={shown("email")}>
        <input {...fieldAria("invite-email", { error: shown("email") })} type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} data-autofocus />
      </FormField>
      <FormField id="invite-role" label={t("users.role")} hint={t(`users.roleHelp.${role}`)}>
        <select {...fieldAria("invite-role", { hint: true })} value={role} onChange={(e) => setRole(e.target.value as TenantRole)}>
          {roles.map((r) => (
            <option key={r} value={r}>
              {t(`role.${r}`)}
            </option>
          ))}
        </select>
      </FormField>
      <fieldset className="fieldset" disabled={isAdminRole} aria-describedby={isAdminRole ? "invite-scope-admin" : undefined}>
        <legend>{t("users.scope")}</legend>
        <label className="check-row">
          <input type="radio" name="invite-scope" checked={isAdminRole || scope === "all"} onChange={() => setScope("all")} />
          <span>{t("users.allSites")}</span>
        </label>
        <label className="check-row">
          <input type="radio" name="invite-scope" checked={!isAdminRole && scope === "sites"} onChange={() => setScope("sites")} />
          <span>{t("invite.selectedSites")}</span>
        </label>
        {isAdminRole && (
          <p id="invite-scope-admin" className="field-hint">
            {t("invite.adminAllSites")}
          </p>
        )}
        {!isAdminRole && scope === "sites" && (
          <div className="site-checklist" role="group" aria-label={t("invite.selectedSites")}>
            {sites.map((s) => (
              <label key={s.id} className="check-row">
                <input
                  type="checkbox"
                  checked={siteIds.includes(s.id)}
                  onChange={(e) => setSiteIds((xs) => (e.target.checked ? [...xs, s.id] : xs.filter((x) => x !== s.id)))}
                />
                <span>{s.label}</span>
              </label>
            ))}
            <p className="field-hint">{t("invite.scopeHint")}</p>
          </div>
        )}
        {shown("scope") && <p className="field-error">{shown("scope")}</p>}
      </fieldset>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

/* ---------------- alarm e-mail setting ---------------- */

/**
 * Tenant setting "admins receive a copy of every alarm e-mail" (tenants.alarm_copy_admins, ADR 0011).
 * Readable by every member; only admins (and platform admins) can change it, the database checks again.
 */
function AlarmMailCard({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [on, setOn] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let alive = true;
    setOn(null);
    setLoadError(false);
    provider
      .getAlarmCopyAdmins(tenantId)
      .then((v) => alive && setOn(v))
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
  }, [provider, tenantId]);

  const change = async (next: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await provider.setAlarmCopyAdmins(tenantId, next);
      setOn(next);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title={t("users.alarmMail.title")}>
      {loadError ? (
        <p className="muted small">{t("common.loadError")}</p>
      ) : on === null ? (
        <div className="loading" role="status">
          <span className="spinner" aria-hidden="true" />
          {t("common.loading")}
        </div>
      ) : (
        <label className="check-row">
          <input
            type="checkbox"
            role="switch"
            checked={on}
            disabled={!canEdit || busy}
            onChange={(e) => void change(e.target.checked)}
            aria-describedby="alarm-copy-admins-hint"
          />
          <span>
            <span className="strong">{t("users.alarmMail.copyAdmins")}</span>
            <span id="alarm-copy-admins-hint" className="field-hint">
              {t("users.alarmMail.hint")}
            </span>
          </span>
        </label>
      )}
      {error !== null && <FormError error={error} />}
      {!canEdit && <p className="panel-footnote">{t("users.alarmMail.readOnly")}</p>}
    </Panel>
  );
}

/* ---------------- screen ---------------- */

type Modal = { kind: "invite" } | { kind: "role"; member: Member } | { kind: "remove"; member: Member } | { kind: "revoke"; invitation: Invitation } | null;

export function Users({ data }: { data: TenantData }) {
  const { t, locale } = useI18n();
  const { provider, tenant, user, role, refresh } = useSession();
  const [modal, setModal] = useState<Modal>(null);
  const [invitations, setInvitations] = useState<Invitation[] | null>(null);
  const [invError, setInvError] = useState(false);
  const members = [...(data.members ?? [])].sort(
    (a, b) => TENANT_ROLES.indexOf(b.role) - TENANT_ROLES.indexOf(a.role) || a.displayName.localeCompare(b.displayName, "tr"),
  );
  const siteName = (id: string) => data.sites.find((s) => s.id === id)?.name;
  const tenantId = tenant?.id;

  const loadInvitations = useCallback(async () => {
    if (!tenantId) return;
    try {
      setInvitations(await provider.listInvitations(tenantId));
      setInvError(false);
    } catch {
      setInvError(true);
    }
  }, [provider, tenantId]);

  useEffect(() => {
    setInvitations(null);
    void loadInvitations();
  }, [loadInvitations]);

  const manageable = (m: Member) => !!user && canManageMember(user, role, m);
  const canRevoke = (i: Invitation) => !!user && (user.platformAdmin || i.role !== "admin");
  const anyActions = members.some(manageable);
  const scopeText = (ids: string[]) => {
    if (ids.length === 0) return <span className="muted">{t("users.allSites")}</span>;
    const names = ids.map(siteName).filter(Boolean) as string[];
    return <span title={names.join(", ")}>{names.length > 0 ? names.join(", ") : t("users.siteCount", { count: ids.length })}</span>;
  };

  return (
    <>
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle", { count: members.length })}
        actions={
          <button type="button" className="button button-primary" onClick={() => setModal({ kind: "invite" })}>
            <Icon name="mail" size={16} />
            {t("invite.open")}
          </button>
        }
      />
      <div className="users-layout">
        <div className="stack">
          <Panel flush>
            {members.length === 0 ? (
              <p className="table-empty">{t("users.empty")}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t("users.name")}</th>
                      <th scope="col" className="col-hide-sm">
                        {t("users.email")}
                      </th>
                      <th scope="col">{t("users.role")}</th>
                      <th scope="col" className="col-hide-md">
                        {t("users.scope")}
                      </th>
                      {anyActions && (
                        <th scope="col" className="col-actions">
                          <span className="sr-only">{t("common.actions")}</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.userId}>
                        <td>
                          <span className="user-cell">
                            <span className="avatar avatar-sm" aria-hidden="true">
                              {initials(m.displayName)}
                            </span>
                            <span className="strong">{m.displayName}</span>
                            {m.userId === user?.id && <span className="muted small">{t("members.you")}</span>}
                          </span>
                        </td>
                        <td className="muted small nowrap col-hide-sm">{m.email}</td>
                        <td>
                          <RoleBadge role={m.role} />
                        </td>
                        <td className="col-hide-md">{scopeText(m.siteIds)}</td>
                        {anyActions && (
                          <td className="col-actions">
                            {manageable(m) && (
                              <span className="row-actions">
                                <button type="button" className="icon-button" onClick={() => setModal({ kind: "role", member: m })} title={t("members.changeRole")}>
                                  <Icon name="edit" size={17} label={`${t("members.changeRole")}: ${m.displayName}`} />
                                </button>
                                <button type="button" className="icon-button" onClick={() => setModal({ kind: "remove", member: m })} title={t("members.remove")}>
                                  <Icon name="trash" size={17} label={`${t("members.remove")}: ${m.displayName}`} />
                                </button>
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title={t("invitations.title")} flush>
            {invError ? (
              <p className="table-empty">{t("common.loadError")}</p>
            ) : !invitations ? (
              <p className="table-empty">{t("common.loading")}</p>
            ) : invitations.length === 0 ? (
              <p className="table-empty">{t("invitations.empty")}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t("users.email")}</th>
                      <th scope="col">{t("users.role")}</th>
                      <th scope="col" className="col-hide-md">
                        {t("users.scope")}
                      </th>
                      <th scope="col">{t("invitations.status")}</th>
                      <th scope="col" className="col-hide-sm">
                        {t("invitations.expires")}
                      </th>
                      <th scope="col" className="col-actions">
                        <span className="sr-only">{t("common.actions")}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((i) => (
                      <tr key={i.id}>
                        <td className="small">{i.email}</td>
                        <td>
                          <RoleBadge role={i.role} />
                        </td>
                        <td className="col-hide-md">{scopeText(i.siteIds)}</td>
                        <td>
                          <span className={`badge tone-${INVITE_TONE[i.status]}`}>{t(`invitationStatus.${i.status}`)}</span>
                        </td>
                        <td className="col-hide-sm nowrap small" title={formatDate(i.expiresAt, locale) ?? undefined}>
                          {i.status === "pending" ? relativeTime(i.expiresAt, locale) : t("common.empty")}
                        </td>
                        <td className="col-actions">
                          {i.status === "pending" && canRevoke(i) && (
                            <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "revoke", invitation: i })}>
                              {t("invitations.revoke")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
        <div className="stack">
          <Panel title={t("users.rolesTitle")}>
            <ul className="role-legend">
              {[...TENANT_ROLES].reverse().map((r) => (
                <li key={r}>
                  <RoleBadge role={r} />
                  <span>{t(`users.roleHelp.${r}`)}</span>
                </li>
              ))}
            </ul>
          </Panel>
          {tenantId && <AlarmMailCard tenantId={tenantId} canEdit={!!user?.platformAdmin || isTenantAdmin(role)} />}
          {!user?.platformAdmin && <Note>{t("members.adminSeatsNote")}</Note>}
        </div>
      </div>

      {modal?.kind === "invite" && <InviteDialog data={data} onClose={() => setModal(null)} onCreated={() => void loadInvitations()} />}
      {modal?.kind === "role" && <RoleDialog member={modal.member} onClose={() => setModal(null)} />}
      {modal?.kind === "remove" && (
        <ConfirmDialog
          title={t("members.removeTitle", { name: modal.member.displayName })}
          confirmLabel={t("members.remove")}
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            if (!tenant) return;
            await provider.removeMember(tenant.id, modal.member.userId);
            await refresh();
            setModal(null);
          }}
        >
          <p>{t("members.removeBody", { tenant: tenant?.name ?? "" })}</p>
        </ConfirmDialog>
      )}
      {modal?.kind === "revoke" && (
        <ConfirmDialog
          title={t("invitations.revokeTitle", { email: modal.invitation.email })}
          confirmLabel={t("invitations.revoke")}
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await provider.revokeInvitation(modal.invitation.id);
            await loadInvitations();
            setModal(null);
          }}
        >
          <p>{t("invitations.revokeBody")}</p>
        </ConfirmDialog>
      )}
    </>
  );
}
